import { registerLocaleData } from '@angular/common';
import { SiLocaleConfig } from '@siemens/element-ng/localization';

/**
 * Available locales for the application.
 * Add or remove locales as needed, ensuring the webpack include pattern below matches.
 */
export const AVAILABLE_LOCALES = ['de', 'en'] as const;
export type AvailableLocale = (typeof AVAILABLE_LOCALES)[number];

export const DEFAULT_LOCALE: AvailableLocale = 'de';

/**
 * Dynamically loads and registers Angular locale data for the specified locale.
 * This function is called on every locale change.
 *
 * Note: The webpackInclude regex must match all supported locales.
 * If you add new locales, update the pattern accordingly.
 */
const localeInitializer = async (localeId: string): Promise<void> => {
    // Dynamic import of locale data based on locale ID
    // Using switch statement for explicit imports to satisfy module resolution
    let localeModule: { default: unknown };

    switch (localeId) {
        case 'en':
            localeModule = await import('@angular/common/locales/en');
            break;
        case 'de':
        default:
            localeModule = await import('@angular/common/locales/de');
            break;
    }

    registerLocaleData(localeModule.default);
};

/**
 * SiLocaleConfig configuration for the Element framework.
 *
 * @property availableLocales - List of supported locales (e.g., 'en', 'de', 'en-GB')
 * @property defaultLocale - Fallback locale when no user preference is available
 * @property localeInitializer - Function to register Angular locale data
 * @property dynamicLanguageChange - If false (default), changing locale triggers page reload
 * @property fallbackEnabled - If true, ngx-translate uses defaultLocale translations as fallback
 */
export const localeConfig: SiLocaleConfig = {
    availableLocales: [...AVAILABLE_LOCALES],
    defaultLocale: DEFAULT_LOCALE,
    localeInitializer,
    dynamicLanguageChange: true,
    fallbackEnabled: true
};
